import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  getQuestionnaire, saveQuestionnaire, generateFromQuestionnaire,
  getJobStatus, exportQuestionnaire,
} from '../services/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, FileSignature, Copy, Download, Plus,
  HelpCircle, Sparkles, Check, Pencil, X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { SLOW_AFTER_MIN } from '../components/upload/JobProgressTracker';

// ---------------------------------------------------------------------------
// Field accessors — the backend question/theme shape may vary slightly, so read
// through a few likely field names and always write answers back to `.answer`.
// ---------------------------------------------------------------------------
const themeName = (t) => t?.name ?? t?.title ?? t?.theme ?? 'Questions';
const qText = (q) => q?.text ?? q?.question ?? '';
const qWhy = (q) => q?.why ?? q?.why_we_ask ?? q?.rationale ?? '';

const hasThemes = (data) => Array.isArray(data?.themes) && data.themes.length > 0;

/**
 * SOW Discovery Questionnaire page.
 *
 * Shows the AI-generated discovery questions grouped by theme. Each question
 * carries a "Why we ask" rationale and an answer box (debounced autosave).
 * The user can add custom questions, copy the question list, download it as
 * Word, or generate the SOW document folding the answers in — unanswered
 * questions become documented assumptions in the draft.
 */
const SowQuestionnairePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const routeJobId = location.state?.job_id || null;

  const [questionnaire, setQuestionnaire] = useState(null);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  // "themeIdx-qIdx" of the AI question being edited inline, or null.
  const [editingKey, setEditingKey] = useState(null);

  // SOW-generation job tracking
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [genElapsedMin, setGenElapsedMin] = useState(0);

  const genPollRef = useRef(null);
  const genTimerRef = useRef(null);
  const questionPollRef = useRef(null);
  const saveTimerRef = useRef(null);
  const themesRef = useRef([]);

  // Keep a live ref of themes so debounced saves and generate always send the
  // latest answers without stale-closure surprises.
  useEffect(() => { themesRef.current = themes; }, [themes]);

  const clearAllTimers = () => {
    if (genPollRef.current) clearInterval(genPollRef.current);
    if (genTimerRef.current) clearInterval(genTimerRef.current);
    if (questionPollRef.current) clearInterval(questionPollRef.current);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    genPollRef.current = null;
    genTimerRef.current = null;
    questionPollRef.current = null;
    saveTimerRef.current = null;
  };

  const applyData = useCallback((data) => {
    setQuestionnaire(data);
    setThemes(Array.isArray(data?.themes) ? data.themes : []);
  }, []);

  // ---- Poll for question generation to finish, then load the themes ----
  const startQuestionPolling = useCallback((jobId) => {
    if (questionPollRef.current) clearInterval(questionPollRef.current);
    setGeneratingQuestions(true);
    const startTime = Date.now();

    questionPollRef.current = setInterval(async () => {
      // Deadline guard — a stalled backend job shouldn't spin forever.
      if (Date.now() - startTime > 12 * 60 * 1000) {
        clearInterval(questionPollRef.current);
        questionPollRef.current = null;
        setGeneratingQuestions(false);
        toast.error('Generating questions is taking longer than expected — the job may have stalled. Please try again.');
        return;
      }
      try {
        // Check the job (for failure), then re-fetch the questionnaire — themes
        // arriving is the real "done" signal.
        if (jobId) {
          const jobRes = await getJobStatus(jobId);
          if (jobRes.data.status === 'failed') {
            clearInterval(questionPollRef.current);
            questionPollRef.current = null;
            setGeneratingQuestions(false);
            toast.error(jobRes.data.error || 'Failed to generate discovery questions');
            return;
          }
        }
        const res = await getQuestionnaire(id);
        if (hasThemes(res.data)) {
          clearInterval(questionPollRef.current);
          questionPollRef.current = null;
          applyData(res.data);
          setGeneratingQuestions(false);
          toast.success('Discovery questions ready!');
        } else if (res.data?.status === 'failed') {
          // Failed with no job id available to poll (e.g. hard refresh) — stop.
          clearInterval(questionPollRef.current);
          questionPollRef.current = null;
          setGeneratingQuestions(false);
          toast.error('Failed to generate discovery questions');
        }
      } catch (err) {
        console.error('Question poll error:', err);
      }
    }, 3000);
  }, [id, applyData]);

  // ---- Initial load ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getQuestionnaire(id);
        if (cancelled) return;
        applyData(res.data);
        if (res.data?.status === 'failed') {
          // Question generation already failed server-side (e.g. missing API key
          // or an unusable model reply) — surface it instead of spinning.
          setGeneratingQuestions(false);
          toast.error('Discovery question generation failed. Please try again from the SOW Builder.');
        } else if (!hasThemes(res.data) && (res.data?.status === 'generating' || routeJobId || res.data?.job_id)) {
          startQuestionPolling(routeJobId || res.data?.job_id);
        }
      } catch (error) {
        toast.error('Failed to load the questionnaire');
        navigate('/sow-builder');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; clearAllTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---- Debounced autosave ----
  const scheduleSave = useCallback((nextThemes) => {
    setSaveState('saving');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveQuestionnaire(id, { themes: nextThemes });
        setSaveState('saved');
      } catch (err) {
        // Keep it simple + 409-free: no version is sent, so a concurrent-edit
        // 409 is not expected. Surface anything else quietly and let the next
        // edit retry.
        console.error('Autosave error:', err);
        setSaveState('idle');
      }
    }, 800);
  }, [id]);

  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    try {
      setSaveState('saving');
      await saveQuestionnaire(id, { themes: themesRef.current });
      setSaveState('saved');
    } catch (err) {
      console.error('Flush save error:', err);
      setSaveState('idle');
    }
  }, [id]);

  const updateAnswer = (themeIdx, qIdx, value) => {
    setThemes((prev) => {
      const next = prev.map((t, ti) => {
        if (ti !== themeIdx) return t;
        const questions = (t.questions || []).map((q, qi) =>
          qi === qIdx ? { ...q, answer: value } : q
        );
        return { ...t, questions };
      });
      scheduleSave(next);
      return next;
    });
  };

  const addCustomQuestion = (themeIdx) => {
    setThemes((prev) => {
      const next = prev.map((t, ti) => {
        if (ti !== themeIdx) return t;
        const newQ = {
          id: `custom-${Date.now()}`,
          question: '',
          why: 'Custom question added by the consultant.',
          answer: '',
          custom: true,
        };
        return { ...t, questions: [...(t.questions || []), newQ] };
      });
      scheduleSave(next);
      return next;
    });
  };

  const removeQuestion = (themeIdx, qIdx) => {
    setThemes((prev) => {
      const next = prev.map((t, ti) => {
        if (ti !== themeIdx) return t;
        return { ...t, questions: (t.questions || []).filter((_, qi) => qi !== qIdx) };
      });
      scheduleSave(next);
      return next;
    });
    setEditingKey(null);
  };

  const updateQuestionText = (themeIdx, qIdx, value) => {
    setThemes((prev) => {
      const next = prev.map((t, ti) => {
        if (ti !== themeIdx) return t;
        const questions = (t.questions || []).map((q, qi) =>
          qi === qIdx ? { ...q, question: value } : q
        );
        return { ...t, questions };
      });
      scheduleSave(next);
      return next;
    });
  };

  // ---- Toolbar: copy questions (markdown export text -> clipboard) ----
  const handleCopyQuestions = async () => {
    if (!token) return;
    try {
      const res = await fetch(exportQuestionnaire(id, 'md'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const text = await res.text();
      await navigator.clipboard.writeText(text);
      toast.success('Questions copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy questions');
    }
  };

  // ---- Toolbar: download Word (authenticated blob) ----
  const handleDownloadWord = async () => {
    if (!token) return;
    try {
      const res = await fetch(exportQuestionnaire(id, 'docx'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${questionnaire?.title || 'discovery-questions'}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Questions downloaded as Word!');
    } catch (err) {
      toast.error('Failed to download questions');
    }
  };

  // ---- Primary: generate the SOW document from the answers ----
  const handleGenerate = async () => {
    if (generatingDoc) return;
    // Persist the latest answers before drafting so nothing is lost.
    await flushSave();

    setGeneratingDoc(true);
    setGenElapsedMin(0);
    const startTime = Date.now();
    if (genTimerRef.current) clearInterval(genTimerRef.current);
    genTimerRef.current = setInterval(() => {
      setGenElapsedMin(Math.floor((Date.now() - startTime) / 60000));
    }, 30000);

    const stopGen = () => {
      if (genPollRef.current) clearInterval(genPollRef.current);
      if (genTimerRef.current) clearInterval(genTimerRef.current);
      genPollRef.current = null;
      genTimerRef.current = null;
      setGeneratingDoc(false);
      setGenElapsedMin(0);
    };

    try {
      const res = await generateFromQuestionnaire(id);
      const jobId = res.data.job_id;
      toast.info('Drafting the scope of work from your answers...');
      let pollFails = 0;
      genPollRef.current = setInterval(async () => {
        // SOW drafting is two Opus passes (analysis + writer) — same 20-min deadline as design.
        if (Date.now() - startTime > 20 * 60 * 1000) {
          stopGen();
          toast.error('This is taking longer than expected — the job may have stalled. Please try again.');
          return;
        }
        try {
          const jobRes = await getJobStatus(jobId);
          const job = jobRes.data;
          pollFails = 0;
          if (job.status === 'complete') {
            stopGen();
            const docId = job.documentation_id;
            toast.success('SOW draft ready!');
            if (docId) navigate(`/sow-builder/${docId}`);
          } else if (job.status === 'failed') {
            stopGen();
            toast.error(job.error || 'SOW drafting failed');
          }
        } catch (err) {
          pollFails += 1;
          console.error('Generate poll error:', err);
          if (pollFails >= 5) {
            stopGen();
            toast.error('Lost connection to the drafting job');
          }
        }
      }, 3000);
    } catch (error) {
      stopGen();
      toast.error(error.response?.data?.detail || 'Failed to start SOW drafting');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  // ---- Questions still being generated ----
  if (generatingQuestions && !hasThemes({ themes })) {
    return (
      <Layout>
        <div className="p-8 max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate('/sow-builder')} data-testid="questions-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="questions-page-title">
                Discovery Questions
              </h1>
              <p className="text-slate-500 text-sm">{questionnaire?.title}</p>
            </div>
          </div>
          <Card className="border-blue-200">
            <CardContent className="p-8 flex flex-col items-center text-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
              <p className="text-sm font-medium text-slate-700">Generating discovery questions...</p>
              <p className="text-xs text-slate-500 max-w-sm">
                The AI is reading your inputs and preparing the questions worth asking the customer before we scope. This usually takes under a minute.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const totalQuestions = themes.reduce((n, t) => n + (t.questions?.length || 0), 0);
  const answeredQuestions = themes.reduce(
    (n, t) => n + (t.questions?.filter(q => (q.answer || '').trim()).length || 0), 0
  );

  return (
    <Layout>
      <div className="p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/sow-builder')} data-testid="questions-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] truncate" data-testid="questions-page-title">
              Discovery Questions
            </h1>
            <p className="text-slate-500 text-sm truncate">{questionnaire?.title}</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Button variant="outline" size="sm" onClick={handleCopyQuestions} data-testid="questions-copy-btn" className="h-8 text-xs">
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy questions
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadWord} data-testid="questions-download-word-btn" className="h-8 text-xs">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download Word
          </Button>
          <div className="ml-auto flex items-center gap-3 text-xs text-slate-400">
            {saveState === 'saving' && (
              <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving...</span>
            )}
            {saveState === 'saved' && (
              <span className="flex items-center gap-1 text-emerald-600"><Check className="h-3 w-3" /> Saved</span>
            )}
            <span>{answeredQuestions}/{totalQuestions} answered</span>
          </div>
        </div>

        {/* Assumption note */}
        <div className="mb-6 rounded-lg bg-sky-50/60 border border-sky-100 px-4 py-2.5 text-xs text-slate-600">
          Any questions you leave blank become clearly documented assumptions in the generated SOW — answer what you can and generate whenever you're ready.
        </div>

        {/* Themes */}
        <div className="space-y-6">
          {themes.map((theme, ti) => (
            <Card key={ti} className="border-slate-200" data-testid={`questions-theme-${ti}`}>
              <CardHeader>
                <CardTitle className="text-base font-['Plus_Jakarta_Sans']">{themeName(theme)}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {(theme.questions || []).map((q, qi) => (
                  <div key={q.id ?? qi} className="space-y-2" data-testid={`questions-item-${ti}-${qi}`}>
                    {(q.custom || editingKey === `${ti}-${qi}`) ? (
                      <div className="flex items-start gap-1.5">
                        <Input
                          value={qText(q)}
                          onChange={(e) => updateQuestionText(ti, qi, e.target.value)}
                          onBlur={() => setEditingKey(null)}
                          autoFocus={editingKey === `${ti}-${qi}`}
                          placeholder="Type your question..."
                          className="font-medium"
                          data-testid={`questions-custom-text-${ti}-${qi}`}
                        />
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => removeQuestion(ti, qi)}
                          title="Delete this question"
                          className="mt-2 shrink-0 text-slate-300 hover:text-red-500"
                          data-testid={`questions-delete-${ti}-${qi}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="group flex items-start gap-1.5">
                        <p className="text-sm font-medium text-slate-800">{qText(q)}</p>
                        <button
                          type="button"
                          onClick={() => setEditingKey(`${ti}-${qi}`)}
                          title="Edit this question"
                          className="mt-0.5 shrink-0 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-[#2B8FD8] transition-opacity"
                          data-testid={`questions-edit-${ti}-${qi}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeQuestion(ti, qi)}
                          title="Delete this question"
                          className="mt-0.5 shrink-0 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity"
                          data-testid={`questions-delete-${ti}-${qi}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    {qWhy(q) && (
                      <p className="text-xs italic text-slate-500 flex items-start gap-1.5">
                        <HelpCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
                        <span>Why we ask: {qWhy(q)}</span>
                      </p>
                    )}
                    <textarea
                      value={q.answer || ''}
                      onChange={(e) => updateAnswer(ti, qi, e.target.value)}
                      placeholder="Your answer (optional)..."
                      rows={3}
                      className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B8FD8] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                      data-testid={`questions-answer-${ti}-${qi}`}
                    />
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addCustomQuestion(ti)}
                  className="h-7 text-xs text-[#2B8FD8] hover:text-[#2075b3] hover:bg-blue-50"
                  data-testid={`questions-add-custom-${ti}`}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add a question
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Generate */}
        <div className="mt-8">
          <Button
            className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] h-12 text-base"
            onClick={handleGenerate}
            disabled={generatingDoc}
            data-testid="questions-generate-btn"
          >
            {generatingDoc ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {genElapsedMin >= 5 ? `Drafting... (${genElapsedMin} min)` : 'Drafting scope of work...'}
              </>
            ) : (
              <>
                <FileSignature className="h-5 w-5 mr-2" />
                Generate document with answers
              </>
            )}
          </Button>
          {generatingDoc && (
            <p className="text-xs text-center text-slate-500 mt-2 flex items-center justify-center gap-1">
              <Sparkles className="h-3 w-3" />
              {genElapsedMin >= SLOW_AFTER_MIN
                ? `This is taking longer than usual (${genElapsedMin} min so far, normally 5 to 8). Leave it running — you will be taken to the draft when it is ready.`
                : `This normally takes 5 to 8 minutes (${genElapsedMin >= 1 ? `${genElapsedMin} min so far` : 'just started'}). You will be taken to the draft when it is ready.`}
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SowQuestionnairePage;
